/**
 * Database API
 * API endpoints for database environment management
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../index';

export interface DatabaseConfig {
  environment: 'local' | 'production';
  host: string;
  name: string;
  user: string;
  dialect: string;
}

export interface DatabaseStatus {
  currentEnvironment: 'local' | 'production';
  configurations: {
    local: DatabaseConfig | null;
    production: DatabaseConfig | null;
  };
  requiresRestart: boolean;
}

export interface SwitchDatabaseResponse {
  success: boolean;
  message: string;
  data: {
    currentEnvironment: 'local' | 'production';
    requiresRestart: boolean;
    restartInstructions?: {
      api: string;
      web: string;
      admin: string;
    };
  };
}

export interface ColumnDifference {
  table: string;
  column: string;
  inLocal: boolean;
  inProduction: boolean;
  localDefinition?: string;
  productionDefinition?: string;
}

export interface IndexDifference {
  table: string;
  index: string;
  inLocal: boolean;
  inProduction: boolean;
  difference?: string;
}

export interface ForeignKeyDifference {
  table: string;
  foreignKey: string;
  inLocal: boolean;
  inProduction: boolean;
  difference?: string;
}

export interface SchemaComparison {
  tablesOnlyInLocal: string[];
  tablesOnlyInProduction: string[];
  tablesInBoth: string[];
  columnDifferences: ColumnDifference[];
  indexDifferences: IndexDifference[];
  foreignKeyDifferences: ForeignKeyDifference[];
  triggersOnlyInLocal: string[];
  triggersOnlyInProduction: string[];
  viewsOnlyInLocal: string[];
  viewsOnlyInProduction: string[];
}

export interface SchemaComparisonResponse {
  comparison: SchemaComparison;
  summary: {
    hasDifferences: boolean;
    totalDifferences: number;
  };
}

export const databaseApi = createApi({
  reducerPath: 'databaseApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/database',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['DatabaseStatus'],
  endpoints: (builder) => ({
    getDatabaseStatus: builder.query<DatabaseStatus, void>({
      query: () => '/status',
      transformResponse: (response: { data: DatabaseStatus }) => response.data,
      providesTags: ['DatabaseStatus'],
    }),
    switchDatabase: builder.mutation<SwitchDatabaseResponse, { environment: 'local' | 'production' }>({
      query: (body) => ({
        url: '/switch',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DatabaseStatus'],
    }),
    compareSchemas: builder.query<SchemaComparisonResponse, void>({
      query: () => '/compare-schemas',
      transformResponse: (response: { data: SchemaComparisonResponse }) => response.data,
    }),
  }),
});

export const {
  useGetDatabaseStatusQuery,
  useSwitchDatabaseMutation,
  useCompareSchemasQuery,
  useLazyCompareSchemasQuery,
} = databaseApi;

